import { Fragment, useState, type CSSProperties, type ReactNode } from "react";
import { codeLanguage, highlightLine } from "./highlight";
import {
  accordionSpec,
  alertBannerSpec,
  avatarSpec,
  badgeSpec,
  cardSpec,
  chipSpec,
  codeBlockSpec,
  counterSpec,
  dividerSpec,
  linkSpec,
  methodBadgeSpec,
  tableSpec,
  tileSpec,
  tooltipSpec,
  type AlertTone,
  type BadgeSize,
  type BadgeTone,
  type HttpMethod,
} from "../specs/display";
import { mergeVars, typographyClass } from "../specs/types";
import { Button } from "./Button";
import { ChevronDownIcon, CopyIcon } from "./Icons";

/* ------------------------------------------------------------------ Badge */

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
}

export function Badge({ children, tone = badgeSpec.defaults.tone, size = badgeSpec.defaults.size }: BadgeProps) {
  const sizeSpec = badgeSpec.sizes[size];
  return (
    <span
      className={`ui-badge ${typographyClass(sizeSpec.typography)}`}
      style={mergeVars(badgeSpec.base, sizeSpec.vars, badgeSpec.tones[tone]) as CSSProperties}
    >
      {children}
    </span>
  );
}

export function MethodBadge({ method }: { method: HttpMethod }) {
  return (
    <span
      className={`ui-method-badge ${typographyClass(methodBadgeSpec.typography)}`}
      style={mergeVars(methodBadgeSpec.base, methodBadgeSpec.methods[method]) as CSSProperties}
    >
      {method}
    </span>
  );
}

/* ------------------------------------------------------------------- Chip */

export interface ChipProps {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
}

export function Chip({ children, selected = false, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-selected={selected}
      data-static={!onClick}
      className={`ui-chip ${typographyClass(chipSpec.typography)}`}
      style={chipSpec.base as CSSProperties}
    >
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- Divider */

/** Разделитель: точка между элементами строки или горизонтальная линия. */
export function Divider({ orientation = "dot" }: { orientation?: "dot" | "horizontal" }) {
  return (
    <span
      className="ui-divider"
      role="separator"
      data-orientation={orientation === "horizontal" ? "horizontal" : undefined}
      style={dividerSpec.base as CSSProperties}
    />
  );
}

/* ---------------------------------------------------------------- Counter */

export function Counter({ value, limit }: { value: ReactNode; limit?: boolean }) {
  return (
    <span
      className={`ui-counter ${typographyClass(counterSpec.typography)}`}
      data-limit={limit}
      style={counterSpec.base as CSSProperties}
    >
      {value}
    </span>
  );
}

/* ----------------------------------------------------------------- Avatar */

export function Avatar({ children }: { children: ReactNode }) {
  return (
    <span className={`ui-avatar ${typographyClass(avatarSpec.typography)}`} style={avatarSpec.base as CSSProperties}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- Link */

export interface LinkProps {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  target?: string;
}

export function Link({ children, href, onClick, target }: LinkProps) {
  const className = `ui-link ${typographyClass(linkSpec.typography)}`;
  const style = linkSpec.base as CSSProperties;
  if (href) {
    return (
      <a className={className} style={style} href={href} target={target} rel={target ? "noreferrer" : undefined}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={className} style={style} onClick={onClick}>
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------- Tooltip */

export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="ui-tooltip"
      style={tooltipSpec.base as CSSProperties}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && <span className={`ui-tooltip__bubble ${typographyClass(tooltipSpec.typography)}`}>{content}</span>}
    </span>
  );
}

/* ----------------------------------------------------------- AlertBanner */

export interface AlertBannerProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}

export function AlertBanner({ tone = alertBannerSpec.defaults.tone, title, children, action }: AlertBannerProps) {
  return (
    <div
      className="ui-alert"
      // без заголовка алерт однострочный — точка и текст выравниваются по центру
      data-compact={!title}
      style={mergeVars(alertBannerSpec.base, alertBannerSpec.tones[tone]) as CSSProperties}
    >
      <span className="ui-alert__marker" />
      <div className="ui-alert__body">
        {title && (
          <strong className={`ui-alert__title ${typographyClass(alertBannerSpec.titleTypography)}`}>{title}</strong>
        )}
        {children && <span className={typographyClass(alertBannerSpec.textTypography)}>{children}</span>}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------ Card / Tile */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={["ui-card", className].filter(Boolean).join(" ")} style={cardSpec.base as CSSProperties}>
      {children}
    </section>
  );
}

export interface TileProps {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
}

export function Tile({ title, description, meta, selected, onClick }: TileProps) {
  return (
    <button type="button" className="ui-tile" data-selected={selected} onClick={onClick} style={tileSpec.base as CSSProperties}>
      <span className={typographyClass(tileSpec.titleTypography)}>{title}</span>
      {description && <span className={typographyClass(tileSpec.textTypography)}>{description}</span>}
      {meta}
    </button>
  );
}

/* ------------------------------------------------------------------ Table */

export interface TableColumn<Row> {
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
}

export function Table<Row>({ columns, rows, rowKey }: {
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
}) {
  return (
    <div className="ui-table-wrap" style={tableSpec.base as CSSProperties}>
      <table className="ui-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={typographyClass(tableSpec.headTypography)}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column) => (
                <td key={column.key} className={typographyClass(tableSpec.cellTypography)}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------- CodeBlock */

export interface CodeBlockProps {
  code: string;
  copyLabel?: string;
  /** Название языка в шапке. Без него блок рисуется без шапки. */
  language?: string;
  /** Нумеровать строки — для длинных примеров. */
  numbered?: boolean;
}

export function CodeBlock({ code, copyLabel = "Copy", language, numbered }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const lines = numbered ? code.split("\n") : null;
  const syntax = codeLanguage(language);

  const copy = () => {
    void navigator.clipboard?.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const copyButton = (
    <Button size="small" variant="ghost" startIcon={<CopyIcon />} iconOnly={Boolean(language)} onClick={copy}>
      {copied ? "✓" : copyLabel}
    </Button>
  );

  return (
    <div
      className={`ui-code-block ${typographyClass(codeBlockSpec.typography)}`}
      data-plain={!language && !numbered}
      style={codeBlockSpec.base as CSSProperties}
    >
      {language ? (
        <div className={`ui-code-block__head ${typographyClass(codeBlockSpec.headTypography)}`}>
          <span>{language}</span>
          {copyButton}
        </div>
      ) : (
        <div className="ui-code-block__actions">{copyButton}</div>
      )}

      <div className="ui-code-block__body">
        {lines ? (
          <div className="ui-code-block__lines">
            {lines.map((line, index) => (
              <Fragment key={index}>
                <span className="ui-code-block__no">{index + 1}</span>
                <pre>{line ? highlightLine(line, syntax, `l${index}`) : " "}</pre>
              </Fragment>
            ))}
          </div>
        ) : (
          <pre>{highlightLine(code, syntax, "l0")}</pre>
        )}
      </div>
    </div>
  );
}

export function InlineCode({ children }: { children: ReactNode }) {
  return (
    <code className={`ui-code-inline ${typographyClass(codeBlockSpec.typography)}`} style={codeBlockSpec.base as CSSProperties}>
      {children}
    </code>
  );
}

/* ------------------------------------------------------------- Accordion */

export function Accordion({ title, children, defaultOpen = false }: {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ui-accordion" style={accordionSpec.base as CSSProperties}>
      <button
        type="button"
        className={`ui-accordion__head ${typographyClass(accordionSpec.titleTypography)}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        {title}
        <ChevronDownIcon style={{ transform: open ? "rotate(180deg)" : undefined }} />
      </button>
      {open && <div className="ui-accordion__body">{children}</div>}
    </div>
  );
}
